import { api } from '@/lib/api'

import type { AutomationResponse, AutomationResponses } from './types'

/**
 * Get the automations list
 * @param tenant - The tenant ID (use "all" for multi-tenant)
 * @param signal - Optional AbortSignal to abort the request
 * @param mapping_id - Optional mapping ID to filter by
 * @param rule_id - Optional rule ID to filter by
 * @param response_name - Optional response name to filter by
 * @param tenants - Optional tenant IDs for multi-tenant filtering
 */
export const getAutomationsList = async (
	tenant: string,
	signal?: AbortSignal,
	mapping_id?: string,
	rule_id?: string,
	response_name?: string,
	tenants?: string[],
) => {
	let baseURL = `/api/tenants/${tenant}/soar/automation_mappings`
	const searchParams = new URLSearchParams()

	if (mapping_id) {
		// Decode the mapping_id to handle the encoded value from the URL
		searchParams.set('mapping_id', decodeURIComponent(mapping_id))
	}

	if (rule_id) {
		// Decode the rule_id to handle the encoded value from the URL
		searchParams.set('rule_id', decodeURIComponent(rule_id))
	}

	if (response_name) {
		// Decode the response_name to handle the encoded value from the URL
		searchParams.set('response_name', decodeURIComponent(response_name))
	}

	// Only add the search params if there are any
	if (searchParams.size > 0) {
		baseURL += `?${searchParams.toString()}`
	}

	const { data } = await api.get<AutomationResponses>(baseURL, {
		signal,
		tenants: tenants,
	})

	return data
}

/**
 * Create an automation
 * @param tenant - The tenant ID
 * @param automation - The automation to create
 * @returns The updated automation
 */
export const createAutomation = async (
	tenant: string,
	automation: Partial<AutomationResponse>,
) => {
	const { data } = await api.post<string>(
		`/api/tenants/${tenant}/soar/automation_mappings`,
		automation,
	)

	return data
}

/**
 * Update an automation
 * @param tenant - The tenant ID
 * @param mapping_id - The mapping ID
 * @param automation - The automation to update
 * @returns The updated automation
 */
export const updateAutomation = async (
	tenant: string,
	mapping_id: string,
	automation: Partial<AutomationResponse>,
) => {
	const { data } = await api.put<string>(
		`/api/tenants/${tenant}/soar/automation_mappings/${mapping_id}`,
		automation,
	)

	return data
}

/**
 * Delete an automation
 * @param tenant - The tenant ID
 * @param mapping_id - The automation mapping ID to delete
 */
export const deleteAutomation = async (tenant: string, mapping_id: string) => {
	const { data } = await api.delete<string>(
		`/api/tenants/${tenant}/soar/automation_mappings/${mapping_id}`,
	)

	return data
}
